import { TranslocoService } from "@jsverse/transloco";

export type TranslationParams = Record<string, string | number>;

  /** Replace `{param}` / `{0}` / `{}` placeholders used by backend translation catalogs. */
export function applyBraceParams(
  value: string,
  params?: TranslationParams,
): string {
  if (!params) {
    return value;
  }

  let result = value.replaceAll(/\{(\w+)\}/g, (match, token: string) => {
    const replacement = params[token];
    return replacement === undefined || replacement === null
      ? match
      : String(replacement);
  });

  // Unindexed `{}` → first provided param (`0` preferred)
  if (result.includes("{}")) {
    const first = params["0"] ?? Object.values(params)[0];
    if (first !== undefined && first !== null) {
      result = result.replace("{}", String(first));
    }
  }

  return result;
}

/** Read a string from a flat or nested Transloco catalog. */
function readCatalogValue(
  catalog: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  if (!catalog) {
    return undefined;
  }

  const direct = catalog[key];
  if (typeof direct === "string") {
    return direct;
  }

  // Nested catalog fallback (e.g. { ui: { common: { fields: { PRODUCT_LINE: "..." }}}})
  const nested = key.split(".").reduce<unknown>((acc, segment) => {
    if (acc && typeof acc === "object") {
      return (acc as Record<string, unknown>)[segment];
    }
    return undefined;
  }, catalog);

  return typeof nested === "string" ? nested : undefined;
}

/**
 * Resolve a key from the *active* language catalog only.
 *
 * Behavior:
 * - Key present in active language JSON → use that value
 * - Key missing in active language JSON → use the HTML/static English `fallback`
 *   (does NOT fall through to en-US.json for missing keys)
 *
 * Whole-language unavailable (e.g. Spanish JSON missing) is handled separately
 * by TranslationService candidates: requested → previous (n-1) → English.
 */
export function translateOr(
  transloco: TranslocoService | null | undefined,
  key: string,
  fallback: string,
  params?: TranslationParams,
): string {
  if (!transloco) {
    return applyBraceParams(fallback, params);
  }

  const activeLang = transloco.getActiveLang();
  const catalog = transloco.getTranslation(activeLang) as Record<
    string,
    unknown
  >;
  const activeValue = readCatalogValue(catalog, key);

  // Missing in the active catalog → static HTML English default.
  if (activeValue == null || activeValue.length === 0) {
    return applyBraceParams(fallback, params);
  }

  // Resolve via Transloco so {{param}} interpolation still works.
  const translated = transloco.translate(key, params, activeLang);
  const value =
    !translated || translated === key ? activeValue : String(translated);
  return applyBraceParams(value, params);
}
